import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { AlertTriangle, ChevronRight, GitBranch, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { client } from "@/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export const Route = createFileRoute("/traces/$traceId/")({
  component: TraceDetailPage,
});

async function fetchTrace(traceId: string) {
  const res = await client.api.traces[":traceId"].$get({
    param: { traceId },
  });
  if (!res.ok) throw new Error("Trace not found");
  return res.json();
}

type TraceData = Awaited<ReturnType<typeof fetchTrace>>;
type StepData = TraceData["steps"][number];

function statusVariant(status: string) {
  switch (status) {
    case "completed":
      return "default" as const;
    case "running":
      return "secondary" as const;
    case "error":
      return "destructive" as const;
    default:
      return "outline" as const;
  }
}

function stepStatusVariant(finishReason: string | null) {
  switch (finishReason) {
    case "stop":
    case "tool-calls":
      return "default" as const;
    case "error":
    case "abort":
      return "destructive" as const;
    case "compaction":
      return "outline" as const;
    default:
      return "secondary" as const;
  }
}

function stepStatusLabel(finishReason: string | null) {
  switch (finishReason) {
    case "stop":
    case "tool-calls":
      return "success";
    case "error":
      return "error";
    case "abort":
      return "abort";
    case "compaction":
      return "compaction";
    default:
      return "pending";
  }
}

function formatDuration(ms: number | null) {
  if (ms == null) return "-";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

const DATA_URL_RE = /^data:image\/[^;]+;base64,/;

/** Collect image data URLs from an arbitrarily nested value, replacing them with placeholders. */
function extractImages(value: unknown): {
  cleaned: unknown;
  images: string[];
} {
  const images: string[] = [];
  function walk(v: unknown): unknown {
    if (typeof v === "string" && DATA_URL_RE.test(v)) {
      images.push(v);
      return `<image #${images.length}>`;
    }
    if (Array.isArray(v)) return v.map(walk);
    if (v !== null && typeof v === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v)) {
        out[k] = walk(val);
      }
      return out;
    }
    return v;
  }
  return { cleaned: walk(value), images };
}

function JsonBlock({ data }: { data: unknown }) {
  const { cleaned, images } = extractImages(data);
  return (
    <div className="space-y-2">
      <pre className="max-h-96 overflow-auto rounded bg-muted p-3 text-xs">
        {JSON.stringify(cleaned, null, 2)}
      </pre>
      {images.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`tool image ${i + 1}`}
          className="max-h-96 rounded border"
        />
      ))}
    </div>
  );
}

function CompactionStepCard({ step }: { step: StepData }) {
  const [open, setOpen] = useState(false);
  let meta: {
    tier?: number;
    toolResultsCleared?: number;
    bytesSavedByClearing?: number;
    originalTokenEstimate?: number;
    compactedTokenEstimate?: number;
  } = {};
  try {
    meta = step.text ? JSON.parse(step.text) : {};
  } catch {
    // not JSON — show raw text in expanded view
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer py-3">
            <div className="flex items-center gap-3">
              <ChevronRight
                className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
              />
              <CardTitle className="text-sm font-medium">Compaction</CardTitle>
              <Badge
                variant="outline"
                className="text-xs border-blue-500 text-blue-600"
              >
                tier {meta.tier ?? "?"}
              </Badge>
              {meta.originalTokenEstimate != null &&
                meta.compactedTokenEstimate != null && (
                  <span className="font-mono text-xs text-muted-foreground">
                    ~{Math.round(meta.originalTokenEstimate / 1000)}K &rarr; ~
                    {Math.round(meta.compactedTokenEstimate / 1000)}K tokens
                  </span>
                )}
              <span className="ml-auto font-mono text-xs text-muted-foreground">
                {formatDuration(step.duration_ms)}
              </span>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-2 pt-0">
            {meta.toolResultsCleared != null && (
              <div className="text-sm text-muted-foreground">
                Tool results cleared: {meta.toolResultsCleared}
              </div>
            )}
            {meta.bytesSavedByClearing != null && (
              <div className="text-sm text-muted-foreground">
                Bytes saved by clearing:{" "}
                {Math.round(meta.bytesSavedByClearing / 1024)}KB
              </div>
            )}
            {step.text && !meta.tier && (
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-xs">
                {step.text}
              </pre>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function StepCard({ step }: { step: StepData }) {
  if (step.finish_reason === "compaction") {
    return <CompactionStepCard step={step} />;
  }

  const [open, setOpen] = useState(false);
  const toolCalls = (step.tool_calls ?? []) as Array<{
    name: string;
    id: string;
    args: unknown;
  }>;
  const toolResults = (step.tool_results ?? []) as Array<{
    name: string;
    id: string;
    result?: unknown;
    error?: string;
  }>;
  const hasTools = toolCalls.length > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer py-3">
            <div className="flex items-center gap-3">
              <ChevronRight
                className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
              />
              <CardTitle className="text-sm font-medium">
                Step {step.step_index}
              </CardTitle>
              {hasTools && (
                <div className="flex flex-wrap gap-1">
                  {toolCalls.map((tc, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {tc.name}
                    </Badge>
                  ))}
                </div>
              )}
              <Badge
                variant={stepStatusVariant(step.finish_reason)}
                className="text-xs"
              >
                {stepStatusLabel(step.finish_reason)}
              </Badge>
              <span className="ml-auto font-mono text-xs text-muted-foreground">
                {step.prompt_tokens + step.completion_tokens} tok{" "}
                {formatDuration(step.duration_ms)}
              </span>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            {step.text && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                  Text output
                </h4>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-xs">
                  {step.text}
                </pre>
              </div>
            )}

            {toolCalls.map((tc, i) => {
              const result = toolResults.find((tr) => tr.id === tc.id);
              return (
                <div key={i} className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                    {tc.name}({tc.id})
                  </h4>
                  <div className="grid gap-2 md:grid-cols-2">
                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">Args</p>
                      <JsonBlock data={tc.args} />
                    </div>
                    {result && (
                      <div>
                        <p
                          className={`mb-1 text-xs ${result.error ? "text-destructive" : "text-muted-foreground"}`}
                        >
                          {result.error ? "Error" : "Result"}
                        </p>
                        <JsonBlock data={result.error ?? result.result} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function TraceDetailPage() {
  const { traceId } = Route.useParams();
  const router = useRouter();
  const {
    data: trace,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["trace", traceId],
    queryFn: () => fetchTrace(traceId),
  });
  const deleteTrace = useMutation({
    mutationFn: async () => {
      const res = await client.api.traces[":traceId"].$delete({
        param: { traceId },
      });
      if (!res.ok) throw new Error("Failed to delete trace");
    },
    onSuccess: () => {
      toast.success("Trace deleted");
      router.navigate({ to: "/traces" });
    },
  });

  if (isPending) {
    return (
      <div className="mx-auto min-h-screen max-w-5xl bg-background px-6 py-10">
        <p className="text-muted-foreground">Loading trace...</p>
      </div>
    );
  }

  if (isError || !trace) {
    return (
      <div className="mx-auto min-h-screen max-w-5xl bg-background px-6 py-10">
        <p className="text-destructive">Failed to load trace.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen max-w-5xl bg-background px-6 py-10">
      <div className="mb-6">
        <Link
          to="/traces"
          className="text-sm text-muted-foreground hover:underline"
        >
          All traces
        </Link>
      </div>

      <div className="mb-8 space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {trace.agent_name}
          </h1>
          <Badge variant={statusVariant(trace.status)}>{trace.status}</Badge>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-destructive hover:text-destructive"
            onClick={() => deleteTrace.mutate()}
          >
            <Trash2 className="mr-1 h-4 w-4" />
            Delete
          </Button>
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <span>
            Model: <span className="font-mono">{trace.model_id}</span>
          </span>
          <span>{trace.total_steps} steps</span>
          <span>
            {(trace.prompt_tokens + trace.completion_tokens).toLocaleString()}{" "}
            tokens ({trace.prompt_tokens.toLocaleString()} in /{" "}
            {trace.completion_tokens.toLocaleString()} out)
          </span>
          <span>{formatDuration(trace.duration_ms)}</span>
          <span>{new Date(trace.created_at).toLocaleString()}</span>
        </div>
        {trace.stop_reason === "step_limit" && (
          <div className="flex items-center gap-2 rounded border border-amber-500 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Agent hit its step limit and may not have completed its task. The
            last step was still making tool calls when it was stopped.
          </div>
        )}
        {trace.error && (
          <pre className="rounded bg-destructive/10 p-3 text-sm text-destructive">
            {trace.error}
          </pre>
        )}
        {trace.parent_trace_id && (
          <div className="text-sm text-muted-foreground">
            Parent:{" "}
            <Link
              to="/traces/$traceId"
              params={{ traceId: trace.parent_trace_id }}
              className="font-mono hover:underline"
            >
              {trace.parent_trace_id}
            </Link>
          </div>
        )}
        <p className="font-mono text-xs text-muted-foreground">{trace.id}</p>
      </div>

      {trace.prompt && (
        <div className="mb-6">
          <h2 className="mb-2 text-lg font-semibold">Prompt</h2>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-muted p-4 text-sm">
            {trace.prompt}
          </pre>
        </div>
      )}

      {trace.children && trace.children.length > 0 && (
        <div className="mb-6">
          <h2 className="mb-3 text-lg font-semibold">Sub-agent traces</h2>
          <div className="space-y-2">
            {trace.children.map((child: TraceData["children"][number]) => (
              <Link
                key={child.id}
                to="/traces/$traceId"
                params={{ traceId: child.id }}
                className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50"
              >
                <GitBranch className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{child.agent_name}</span>
                <Badge variant={statusVariant(child.status)}>
                  {child.status}
                </Badge>
                {child.stop_reason === "step_limit" && (
                  <Badge
                    variant="outline"
                    className="border-amber-500 text-amber-600"
                  >
                    <AlertTriangle className="mr-1 h-3 w-3" />
                    step limit
                  </Badge>
                )}
                <span className="font-mono text-xs text-muted-foreground">
                  {child.model_id}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {child.total_steps} steps &middot;{" "}
                  {formatDuration(child.duration_ms)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {trace.steps.map((step) => (
          <StepCard key={step.id} step={step} />
        ))}
        {trace.steps.length === 0 && (
          <p className="text-muted-foreground">
            {trace.status === "running"
              ? "Agent is still running. Refresh to see steps."
              : "No steps recorded."}
          </p>
        )}
      </div>
    </div>
  );
}
